import React from 'react';
import { Brain, FileText, Zap, CheckCircle } from 'lucide-react';

const ParserModeSelector = ({ value, onChange, disabled = false }) => {
  const modes = [
    {
      id: 'standard',
      name: 'Standard Parser',
      icon: FileText,
      description: 'Fast & reliable rules-based extraction',
      features: ['Quick processing', 'High accuracy for standard formats', 'No AI costs'],
      color: 'emerald'
    },
    {
      id: 'ai',
      name: 'AI Parser',
      icon: Brain,
      description: 'Intelligent AI-powered extraction with Bytez',
      features: ['Handles complex formats', 'Better accuracy', 'Smart field detection'],
      color: 'purple',
      badge: 'AI Powered'
    }
  ];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {modes.map((mode) => {
          const Icon = mode.icon;
          const isSelected = value === mode.id;
          
          return (
            <button
              key={mode.id}
              onClick={() => !disabled && onChange(mode.id)}
              disabled={disabled}
              className={`relative p-6 rounded-xl border-2 transition-all duration-300 text-left ${
                isSelected
                  ? `border-${mode.color}-500 bg-${mode.color}-50 shadow-lg transform scale-105`
                  : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-md'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:-translate-y-1'}`}
            >
              {/* Selected Indicator */}
              {isSelected && (
                <div className={`absolute top-3 right-3 w-6 h-6 bg-${mode.color}-500 rounded-full flex items-center justify-center`}>
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
              )}
              
              {/* Badge */}
              {mode.badge && (
                <div className="absolute top-3 left-3 px-2 py-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full">
                  <span className="text-xs font-bold text-white flex items-center gap-1">
                    <Zap className="w-3 h-3" />
                    {mode.badge}
                  </span>
                </div>
              )}
              
              {/* Icon */}
              <div className={`w-14 h-14 bg-gradient-to-br from-${mode.color}-500 to-${mode.color}-600 rounded-xl flex items-center justify-center mb-4 ${
                mode.badge ? 'mt-6' : ''
              }`}>
                <Icon className="w-7 h-7 text-white" />
              </div>
              
              {/* Title */}
              <h3 className={`text-lg font-bold mb-2 ${
                isSelected ? `text-${mode.color}-900` : 'text-slate-900'
              }`}>
                {mode.name}
              </h3>
              
              {/* Description */}
              <p className="text-sm text-slate-600 mb-4">
                {mode.description}
              </p>
              
              {/* Features */}
              <ul className="space-y-2">
                {mode.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2 text-xs text-slate-700">
                    <CheckCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                      isSelected ? `text-${mode.color}-600` : 'text-slate-400'
                    }`} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>
      
      {/* Info Banner */}
      <div className={`p-4 rounded-lg border-2 transition-all duration-300 ${
        value === 'ai'
          ? 'border-purple-200 bg-purple-50'
          : 'border-emerald-200 bg-emerald-50'
      }`}>
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${
            value === 'ai' ? 'bg-purple-100' : 'bg-emerald-100'
          }`}>
            {value === 'ai' ? (
              <Brain className="w-5 h-5 text-purple-600" />
            ) : (
              <FileText className="w-5 h-5 text-emerald-600" />
            )}
          </div>
          <div className="flex-1">
            <p className={`text-sm font-semibold mb-1 ${
              value === 'ai' ? 'text-purple-900' : 'text-emerald-900'
            }`}>
              {value === 'ai' ? '🤖 AI Mode Active' : '📋 Standard Mode Active'}
            </p>
            <p className="text-xs text-slate-600">
              {value === 'ai'
                ? 'Using Bytez AI for intelligent document understanding. Best for complex or non-standard formats.'
                : 'Using fast rules-based extraction. Best for standard UK payslips and bank statements.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParserModeSelector;
